import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { CalculatorFormContainerProps } from "./CalculatorForm.types";

import { CALCULATOR_TYPES } from "constants/calculator";

import { useScreenContext } from "context/screen";

import { getTabsConfig } from "./CalculatorForm.formConfig";

import {
  convertDataToCalculatorApi,
  getInitialValues,
  getZeroInitialValues,
} from "./CalculatorForm.helpers";

import { useCalculatorForm } from "./CalculatorForm.hooks";

import CalculatorForm from "./CalculatorForm";

const CalculatorFormContainer = ({
  className,
  analyticsType,
  activePosition,
  submitLoading,
  isAuthenticated,
  defaultValues,
  isSonderAfaForced,
  variant = CALCULATOR_TYPES.BASIC,
  lastCalculatorHistoryValues,
  onCalculationClick,
  onAnalyticsTypeChange,
  onPositionChange,
}: CalculatorFormContainerProps) => {
  const formRef = useRef(null);

  const { t } = useTranslation();

  const tabsConfig = getTabsConfig({ t, isExpert: variant === CALCULATOR_TYPES.EXPERT });

  const [activeTab, setActiveTab] = useState<string>(tabsConfig[0].value);

  const [calculatorType, setCalculatorType] =
    useState<(typeof CALCULATOR_TYPES)[keyof typeof CALCULATOR_TYPES]>(variant);

  const [pendingCalculatorType, setPendingCalculatorType] = useState<
    (typeof CALCULATOR_TYPES)[keyof typeof CALCULATOR_TYPES] | null
  >(null);

  const { isDesktop } = useScreenContext();
  const {
    loading,
    loans,
    calculationValues,
    stagesOptions,
    loansOptions,
    defaultCalculationValues,
  } = useCalculatorForm({
    isAuthenticated: true,
  });

  useEffect(() => {
    if (isAuthenticated && pendingCalculatorType === CALCULATOR_TYPES.STANDARD) {
      setCalculatorType(pendingCalculatorType);
      setPendingCalculatorType(null);
    }
  }, [isAuthenticated]);

  const handleActiveTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  const handleCalculatorTypeChange = (
    e: React.MouseEvent<HTMLButtonElement>,
    resetForm: () => void
  ) => {
    e.preventDefault();
    e.stopPropagation();

    const nextType =
      calculatorType === CALCULATOR_TYPES.STANDARD
        ? CALCULATOR_TYPES.BASIC
        : CALCULATOR_TYPES.STANDARD;

    if (nextType === CALCULATOR_TYPES.BASIC) {
      resetForm();
    }

    setCalculatorType(nextType);
  };

  const handleCalculatorTypeClick = (value: keyof typeof CALCULATOR_TYPES) => {
    setCalculatorType(value as typeof calculatorType);
  };

  const handleCalculationClick = (values: any,) => {
    const data = convertDataToCalculatorApi(values);
    const noHistoryIndex = Boolean(formRef.current.submitCount === 0 && defaultCalculationValues);

    onCalculationClick({ ...data, noHistoryIndex }, calculatorType);
  };

  const initialValues =
    calculatorType === CALCULATOR_TYPES.LIMITED_PRO
      ? getInitialValues({
        calculationValues,
        loans,
        t,
        defaultValues,
        isSonderAfaForced,
        defaultCalculationValues: defaultCalculationValues,
      })
      : getZeroInitialValues({
        calculationValues,
        loans,
        t,
        defaultCalculationValues: defaultCalculationValues || lastCalculatorHistoryValues,
      });

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLFormElement>,
    submitForm: () => void
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      submitForm();
    }
  };

  const defaultDowntimeDynamicNotEmpty =
    initialValues.downtimePercentageDynamic.value > 1;

  useEffect(() => {
    if (!formRef.current) return;
    if (formRef.current.submitCount !== 0) return;
    if (calculatorType === CALCULATOR_TYPES.LIMITED_PRO) return;
    if (!defaultCalculationValues) return;

    setCalculatorType((defaultCalculationValues as any)?.calculatorType);

    formRef.current.submitForm();
  }, [formRef, calculatorType, defaultCalculationValues]);

  return (
    <CalculatorForm
      t={t}
      analyticsType={analyticsType}
      className={className}
      formRef={formRef}
      loading={loading}
      activePosition={activePosition}
      submitLoading={submitLoading}
      defaultDowntimeDynamicNotEmpty={defaultDowntimeDynamicNotEmpty}
      isAuthenticated={isAuthenticated}
      isDesktop={isDesktop}
      calculatorType={calculatorType}
      activeTab={activeTab}
      loans={loans}
      loansOptions={loansOptions}
      calculationValues={calculationValues}
      initialValues={initialValues}
      tabsConfig={tabsConfig}
      stagesOptions={stagesOptions}
      onActiveTabChange={handleActiveTabChange}
      onPositionChange={onPositionChange}
      onKeyDown={handleKeyDown}
      onCalculatorTypeChange={handleCalculatorTypeChange}
      onCalculatorTypeClick={handleCalculatorTypeClick}
      onCalculationClick={handleCalculationClick}
      onAnalyticsTypeChange={onAnalyticsTypeChange}
    />
  );
};

export default CalculatorFormContainer;
